// Replay file I/O. A replay is the server's UNMASKED duel tape (DuelReplay) — gzipped JSON on
// disk via the native Compression Streams API, so a multi-MB tape lands as a few hundred KB.
import type { DuelReplay } from "@ygoevo/world-protocol";

const safe = (s: string) => s.replace(/[^\w-]+/g, "_").slice(0, 24) || "duelist";

/** Save a replay to disk as `<date>_<p0>_vs_<p1>.evorep` (gzipped JSON). */
export async function downloadReplay(r: DuelReplay): Promise<void> {
  const json = new Blob([JSON.stringify(r)], { type: "application/json" });
  const gz = await new Response(json.stream().pipeThrough(new CompressionStream("gzip"))).blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(gz);
  a.download = `${r.date.slice(0, 10)}_${safe(r.names[0])}_vs_${safe(r.names[1])}.evorep`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Open the native file picker for a replay. Resolves the parsed replay, null when the user
 *  cancels; throws when the picked file isn't a replay. */
export function pickReplayFile(): Promise<DuelReplay | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".evorep,.json,.gz,application/gzip,application/json";
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return resolve(null);
      readReplayFile(f).then(resolve, reject);
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

/** Parse a replay file (gzipped or plain JSON — sniffed by the gzip magic bytes).
 *  Throws when the file isn't a replay. */
export async function readReplayFile(f: File): Promise<DuelReplay> {
  const buf = new Uint8Array(await f.arrayBuffer());
  const text = buf[0] === 0x1f && buf[1] === 0x8b
    ? await new Response(new Blob([buf]).stream().pipeThrough(new DecompressionStream("gzip"))).text()
    : new TextDecoder().decode(buf);
  const r = JSON.parse(text) as DuelReplay;
  if (r?.v !== 1 || !Array.isArray(r.items) || !Array.isArray(r.names)) throw new Error("not a replay file");
  return r;
}
